/**
 * ParcelOverlay Component
 * 
 * Displays parcel boundaries and neighbor parcels on a map.
 * Works with the PropertyMap component to show property lines,
 * neighboring properties, and their details.
 */

import React from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { GeoFeature, NeighborsByTier, GeoJSONGeometry } from '../../shared/types/parcel';

// ============================================
// Types
// ============================================

interface ParcelOverlayProps {
  /** Primary parcel feature */
  primaryParcel?: GeoFeature | null;
  /** Neighbors grouped by tier */
  neighborsByTier?: NeighborsByTier[];
  /** Currently selected parcel ID */
  selectedParcelId?: number | null;
  /** Callback when a parcel is selected */
  onParcelSelect?: (parcel: GeoFeature | null) => void;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Whether to show the legend */
  showLegend?: boolean;
  /** Max tier to display */
  maxTierVisible?: number;
}

interface ParcelLegendProps {
  neighborsByTier?: NeighborsByTier[];
  maxTierVisible: number;
  onTierToggle?: (tier: number) => void;
  visibleTiers?: Set<number>;
}

// ============================================
// Color Configuration
// ============================================

const PARCEL_COLORS = {
  primary: {
    fill: '#22c55e', // Green
    stroke: '#16a34a',
    opacity: 0.4,
  },
  tier1: {
    fill: '#3b82f6', // Blue
    stroke: '#2563eb',
    opacity: 0.3,
  },
  tier2: {
    fill: '#8b5cf6', // Purple
    stroke: '#7c3aed',
    opacity: 0.25,
  },
  tier3: {
    fill: '#f59e0b', // Amber
    stroke: '#d97706',
    opacity: 0.2,
  },
  selected: {
    fill: '#ef4444', // Red
    stroke: '#dc2626',
    opacity: 0.5,
  },
};

export function getTierColor(tier: number) {
  switch (tier) {
    case 0: return PARCEL_COLORS.primary;
    case 1: return PARCEL_COLORS.tier1;
    case 2: return PARCEL_COLORS.tier2;
    default: return PARCEL_COLORS.tier3;
  }
}

// $/Acre bucket colors for value-based visualization
// Uses a gradient from green (low) through yellow/orange to red (high)
export const PRICE_PER_ACRE_BUCKETS = [
  { min: 0, max: 2000, color: '#22c55e', label: '<$2k/ac' },      // Green - low value
  { min: 2000, max: 5000, color: '#84cc16', label: '$2-5k/ac' },   // Lime
  { min: 5000, max: 10000, color: '#eab308', label: '$5-10k/ac' }, // Yellow  
  { min: 10000, max: 20000, color: '#f97316', label: '$10-20k/ac' }, // Orange
  { min: 20000, max: Infinity, color: '#ef4444', label: '>$20k/ac' }, // Red - high value
] as const;

export function getPricePerAcreColor(perAcre: number | null): { fill: string; stroke: string; opacity: number; label: string } {
  if (perAcre == null || isNaN(perAcre)) {
    return { fill: '#6b7280', stroke: '#4b5563', opacity: 0.25, label: 'N/A' }; // Gray for unknown
  }
  
  for (const bucket of PRICE_PER_ACRE_BUCKETS) {
    if (perAcre >= bucket.min && perAcre < bucket.max) {
      return { fill: bucket.color, stroke: bucket.color, opacity: 0.4, label: bucket.label };
    }
  }
  
  // Fallback to highest bucket
  const last = PRICE_PER_ACRE_BUCKETS[PRICE_PER_ACRE_BUCKETS.length - 1];
  return { fill: last.color, stroke: last.color, opacity: 0.4, label: last.label };
}

// ============================================
// Helper Functions
// ============================================

/**
 * Convert GeoJSON geometry to Mapbox GL JS polygon layer format
 */
export function geometryToMapboxSource(
  features: Array<{
    id: number;
    geometry: GeoJSONGeometry;
    tier: number;
    properties: Record<string, any>;
  }>
): any {
  return {
    type: 'FeatureCollection',
    features: features.map((f) => ({
      type: 'Feature',
      id: f.id,
      geometry: f.geometry,
      properties: {
        ...f.properties,
        tier: f.tier,
      },
    })),
  };
}

/**
 * Format currency value
 */
function formatCurrency(value?: number | null): string {
  if (value == null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format acreage
 */
function formatAcreage(acres?: number | null): string {
  if (acres == null) return 'N/A';
  return `${acres.toFixed(2)} ac`;
}

// ============================================
// Parcel Legend Component
// ============================================

export function ParcelLegend({
  neighborsByTier,
  maxTierVisible,
  onTierToggle,
  visibleTiers,
}: ParcelLegendProps) {
  const styles = useStyles();
  const { isMobile } = useResponsive();

  const tierLabels = [
    'Your Property',
    'Adjacent (Tier 1)',
    '1 Away (Tier 2)',
    '2 Away (Tier 3)',
  ];

  return (
    <View
      style={{
        backgroundColor: addColorOpacity(styles.tokens.color.background, 0.95),
        borderRadius: styles.utils.radius.md,
        padding: styles.utils.spacing.sm,
        gap: styles.utils.spacing.xs,
      }}
    >
      <Text
        style={[
          styles.text('sm'),
          {
            fontWeight: styles.utils.typography.weights.semibold,
            marginBottom: styles.utils.spacing.xs,
          },
        ]}
      >
        Parcel Layers
      </Text>

      {[0, 1, 2, 3].filter(t => t <= maxTierVisible).map((tier) => {
        const color = getTierColor(tier);
        const isVisible = visibleTiers ? visibleTiers.has(tier) : true;
        const tierData = tier === 0 
          ? null 
          : neighborsByTier?.find((t) => t.tier === tier);

        return (
          <Pressable
            key={tier}
            onPress={() => onTierToggle?.(tier)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: styles.utils.spacing.sm,
              opacity: isVisible ? 1 : 0.5,
            }}
          >
            <View
              style={{
                width: 16,
                height: 16,
                backgroundColor: color.fill,
                borderWidth: 2,
                borderColor: color.stroke,
                borderRadius: 2,
              }}
            />
            <Text style={[styles.text('xs'), { flex: 1 }]}>
              {tierLabels[tier] || `Tier ${tier}`}
            </Text>
            {tierData && (
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                ({tierData.total_count})
              </Text>
            )}
          </Pressable>
        );
      })}
    </View>
  );
}

// ============================================
// Parcel Info Card Component
// ============================================

interface ParcelInfoCardProps {
  parcel: GeoFeature;
  tier: number;
  isSelected?: boolean;
  onSelect?: () => void;
  onClose?: () => void;
  compact?: boolean;
}

export function ParcelInfoCard({
  parcel,
  tier,
  isSelected,
  onSelect,
  onClose,
  compact = false,
}: ParcelInfoCardProps) {
  const styles = useStyles();
  const { isMobile } = useResponsive();
  const color = getTierColor(tier);

  return (
    <View
      style={{
        backgroundColor: styles.tokens.color.surface,
        borderRadius: styles.utils.radius.md,
        padding: compact ? styles.utils.spacing.sm : styles.utils.spacing.md,
        borderLeftWidth: 4,
        borderLeftColor: color.stroke,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
      }}
    >
      {/* Header */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <View style={{ flex: 1 }}>
          {parcel.owner_name && (
            <Text
              style={[
                styles.text(compact ? 'sm' : 'base'),
                { fontWeight: styles.utils.typography.weights.semibold },
              ]}
              numberOfLines={1}
            >
              {parcel.owner_name}
            </Text>
          )}
          {parcel.site_address && (
            <Text
              style={[
                styles.text('xs'),
                { color: addColorOpacity(styles.tokens.color.onSurface, 0.7) },
              ]}
              numberOfLines={1}
            >
              {parcel.site_address}
            </Text>
          )}
        </View>
        {onClose && (
          <Pressable onPress={onClose} style={{ padding: 4 }}>
            <Ionicons name="close" size={18} color={styles.tokens.color.onSurface} />
          </Pressable>
        )}
      </View>

      {/* Details */}
      {!compact && (
        <View style={{ marginTop: styles.utils.spacing.sm, gap: styles.utils.spacing.xs }}>
          <View style={{ flexDirection: 'row', gap: styles.utils.spacing.lg }}>
            <View>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
                Acreage
              </Text>
              <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.medium }]}>
                {formatAcreage(parcel.area_acres)}
              </Text>
            </View>
            <View>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
                Total Value
              </Text>
              <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.medium }]}>
                {formatCurrency(parcel.total_value)}
              </Text>
            </View>
          </View>

          {parcel.land_use_description && (
            <View>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
                Land Use
              </Text>
              <Text style={[styles.text('sm')]} numberOfLines={1}>
                {parcel.land_use_description}
              </Text>
            </View>
          )}

          {parcel.parcel_id && (
            <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.5) }]}>
              Parcel ID: {parcel.parcel_id}
            </Text>
          )}
        </View>
      )}

      {/* Actions */}
      {onSelect && !isSelected && (
        <Pressable
          onPress={onSelect}
          style={{
            marginTop: styles.utils.spacing.sm,
            paddingVertical: styles.utils.spacing.xs,
            paddingHorizontal: styles.utils.spacing.sm,
            backgroundColor: addColorOpacity(color.fill, 0.2),
            borderRadius: styles.utils.radius.sm,
            alignItems: 'center',
          }}
        >
          <Text style={[styles.text('xs'), { color: color.stroke, fontWeight: styles.utils.typography.weights.medium }]}>
            View on Map
          </Text>
        </Pressable>
      )}
    </View>
  );
}

// ============================================
// Main ParcelOverlay Component
// ============================================

export function ParcelOverlay({
  primaryParcel,
  neighborsByTier,
  selectedParcelId,
  onParcelSelect,
  loading = false,
  error = null,
  showLegend = true,
  maxTierVisible = 2,
}: ParcelOverlayProps) {
  const styles = useStyles();
  const { isMobile } = useResponsive();
  const [visibleTiers, setVisibleTiers] = React.useState<Set<number>>(new Set([0, 1, 2, 3]));

  const handleTierToggle = (tier: number) => {
    setVisibleTiers((prev) => {
      const next = new Set(prev);
      if (next.has(tier)) {
        next.delete(tier);
      } else {
        next.add(tier);
      }
      return next;
    });
  };

  // Find selected parcel details
  const selectedParcel = React.useMemo(() => {
    if (!selectedParcelId) return null;
    if (primaryParcel?.id === selectedParcelId) return { parcel: primaryParcel, tier: 0 };
    for (const tierData of neighborsByTier || []) {
      const found = tierData.neighbors.find((n: any) => n.id === selectedParcelId);
      if (found) return { parcel: found as GeoFeature, tier: tierData.tier };
    }
    return null;
  }, [selectedParcelId, primaryParcel, neighborsByTier]);

  if (loading) {
    return (
      <View style={{ padding: styles.utils.spacing.md, alignItems: 'center' }}>
        <ActivityIndicator size="small" color={styles.tokens.color.primary} />
        <Text style={[styles.text('sm'), { marginTop: styles.utils.spacing.sm }]}>
          Loading parcel data...
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View
        style={{
          padding: styles.utils.spacing.md,
          backgroundColor: addColorOpacity(styles.tokens.color.error, 0.1),
          borderRadius: styles.utils.radius.md,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.sm }}>
          <Ionicons name="warning" size={20} color={styles.tokens.color.error} />
          <Text style={[styles.text('sm'), { color: styles.tokens.color.error }]}>
            {error}
          </Text>
        </View>
      </View>
    );
  }

  if (!primaryParcel) {
    return (
      <View
        style={{
          padding: styles.utils.spacing.md,
          backgroundColor: addColorOpacity(styles.tokens.color.warning, 0.1),
          borderRadius: styles.utils.radius.md,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.sm }}>
          <Ionicons name="map-outline" size={20} color={styles.tokens.color.warning} />
          <Text style={[styles.text('sm')]}>
            No parcel data available for this property.
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={{ gap: styles.utils.spacing.md }}>
      {/* Legend */}
      {showLegend && (
        <ParcelLegend
          neighborsByTier={neighborsByTier}
          maxTierVisible={maxTierVisible}
          onTierToggle={handleTierToggle}
          visibleTiers={visibleTiers}
        />
      )}

      {/* Selected Parcel Info */}
      {selectedParcel && (
        <ParcelInfoCard
          parcel={selectedParcel.parcel}
          tier={selectedParcel.tier}
          isSelected
          onClose={() => onParcelSelect?.(null)}
        />
      )}

      {/* Primary Parcel (if not selected) */}
      {!selectedParcel && primaryParcel && (
        <ParcelInfoCard
          parcel={primaryParcel}
          tier={0}
          onSelect={() => onParcelSelect?.(primaryParcel)}
        />
      )}

      {/* Neighbor Summary */}
      {neighborsByTier && neighborsByTier.length > 0 && (
        <View
          style={{
            backgroundColor: styles.tokens.color.surface,
            borderRadius: styles.utils.radius.md,
            padding: styles.utils.spacing.md,
          }}
        >
          <Text
            style={[
              styles.text('sm'),
              { fontWeight: styles.utils.typography.weights.semibold, marginBottom: styles.utils.spacing.sm },
            ]}
          >
            Neighboring Properties
          </Text>
          
          {neighborsByTier.map((tierData) => (
            <View
              key={tierData.tier}
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingVertical: styles.utils.spacing.xs,
                borderBottomWidth: 1,
                borderBottomColor: styles.tokens.color.border,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.sm }}>
                <View
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: getTierColor(tierData.tier).fill,
                    borderRadius: 2,
                  }}
                />
                <Text style={[styles.text('sm')]}>
                  Tier {tierData.tier}
                </Text>
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.medium }]}>
                  {tierData.total_count} parcels
                </Text>
                <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
                  {formatAcreage(tierData.total_acres)} • {tierData.unique_owners} owners
                </Text>
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

export default ParcelOverlay;

